/**
 * Collapsible "Status & public visibility" guide for the admin venues list —
 * explains when a venue is visible to users vs hidden, and the delete rule.
 * Data lives in `../lib/venueVisibility` (shared with the detail-page banner).
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge } from '@bravemove/ui-web';
import { VENUE_STATUS_ROWS } from '../lib/venueVisibility';

export function VenueStatusGuide({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-hairline bg-surface" data-testid="venue-status-guide">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-2.5 text-start hover:bg-surface-2"
      >
        <span className="text-[13px] font-semibold text-ink">
          {t('admin:venue.f42.visibility.guideTitle', {
            defaultValue: 'Status & public visibility — when do users see a venue?',
          })}
        </span>
        <span className="font-mono text-[13px] text-ink-3" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? (
        <div className="space-y-2 border-t border-hairline px-4 py-3">
          {VENUE_STATUS_ROWS.map((r) => (
            <div key={r.status} className="flex items-start gap-3 text-[12.5px]">
              <Badge tone={r.tone} size="sm" className="mt-0.5 w-20 shrink-0 justify-center">
                {t(`admin:venue.status.${r.status}`, { defaultValue: r.status })}
              </Badge>
              <span className="text-ink-2">
                <span className="font-semibold text-ink">
                  {r.isPublic
                    ? t('admin:venue.f42.visibility.publicTag', { defaultValue: 'Public' })
                    : t('admin:venue.f42.visibility.hiddenTag', { defaultValue: 'Hidden' })}
                </span>
                {` — ${t(r.key, { defaultValue: r.def })}`}
              </span>
            </div>
          ))}
          <p className="border-t border-hairline pt-2 text-[11.5px] text-ink-3">
            {t('admin:venue.f42.visibility.deleteNote', {
              defaultValue:
                'Removing a venue: Delete is allowed only for draft or suspended venues — suspend or merge an active venue first. Suspending already hides it from everyone.',
            })}
          </p>
        </div>
      ) : null}
    </div>
  );
}
